import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import API from '../api';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { getSocket } from '../socket';
import { CHAT_RIGHT_PX } from './ChatSidebarMenu';
import StatusBadge from './StatusBadge';

// 사내 메신저 — 좌측에 대화상대(조직도)/단체대화방 목록, 우측에 선택한 대화가 함께 있는
// 하나의 창으로 통합되어있다. 사내 메신저 아이콘을 누르면 열리고 닫히며, 왼쪽 위 모서리를
// 드래그하면 창 크기를 조절할 수 있다(오른쪽 아래 모서리는 화면 우측하단에 고정된 채 유지).
const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];
const FILE_BASE = (API.defaults.baseURL || '').replace(/\/api\/?$/, '');
const COMPANY_NAME = '주식회사 나인';
// size.width는 "우측 대화 화면"만의 가로폭(B)이다. 좌측 대화상대 목록은 별도의 treeWidth를
// 가지며, 접었다 펼쳐도 대화 화면 폭(B)에는 전혀 영향을 주지 않는다 — 트리가 펼쳐지면 그만큼
// 창 전체 폭이 늘어나고(추가로 나타남), 접으면 그만큼 줄어든다(사라짐).
const MIN_W = 100, MIN_H = 380, MAX_W = 980, MAX_H = 800;
const TREE_MIN = 80, TREE_MAX = 320, TREE_DEFAULT = 220;

export default function ChatMainWindow() {
  const chat = useChat();
  // 기본 창 크기(대화 화면 폭 + 대화상대 목록 기본폭 220을 합친 전체 가로폭이 490px가 되도록)
  const [size, setSize] = useState({ width: 270, height: 560 });
  const [treeWidth, setTreeWidth] = useState(TREE_DEFAULT);
  const [treeCollapsed, setTreeCollapsed] = useState(false);
  const resizeRef = useRef(null);
  const rootRef = useRef(null);

  // 메신저 창 안에서는 브라우저 기본 우클릭 메뉴(뒤로/새로고침/검사 등)가 절대 뜨면 안 된다.
  // 각 요소(메시지 말풍선 등)에 onContextMenu={preventDefault}를 개별로 달아도, React 이벤트
  // 버블링 경로 밖에서(예: 확장프로그램이 주입한 오버레이, 이미지 미리보기 등) 우클릭하면
  // 놓칠 수 있으므로, document에 캡처 단계로 리스너를 걸어 "이 창 영역 안"이면 무조건 막는다.
  useEffect(() => {
    const blockContextMenu = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) e.preventDefault();
    };
    document.addEventListener('contextmenu', blockContextMenu, true);
    return () => document.removeEventListener('contextmenu', blockContextMenu, true);
  }, []);

  // mode: 'corner'(가로+세로 모두) | 'top'(세로만, 위쪽 변) | 'left'(가로만, 왼쪽 변, 대화화면 폭 B)
  //     | 'tree'(좌측 대화상대 목록 폭만 조절, 최소 80px)
  const startResize = (mode) => (e) => {
    e.preventDefault();
    resizeRef.current = { mode, x: e.clientX, y: e.clientY, w: size.width, h: size.height, tw: treeWidth };
    window.addEventListener('mousemove', onResizeMove);
    window.addEventListener('mouseup', stopResize);
  };
  const onResizeMove = (e) => {
    const r = resizeRef.current;
    if (!r) return;
    if (r.mode === 'tree') {
      setTreeWidth(Math.min(TREE_MAX, Math.max(TREE_MIN, r.tw + (e.clientX - r.x))));
      return;
    }
    const next = { width: size.width, height: size.height };
    if (r.mode === 'corner' || r.mode === 'left') {
      next.width = Math.min(MAX_W, Math.max(MIN_W, r.w + (r.x - e.clientX)));
    }
    if (r.mode === 'corner' || r.mode === 'top') {
      next.height = Math.min(MAX_H, Math.max(MIN_H, r.h + (r.y - e.clientY)));
    }
    setSize(next);
  };
  const stopResize = () => {
    resizeRef.current = null;
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', stopResize);
  };
  useEffect(() => () => {
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', stopResize);
  }, []);

  if (!chat || !chat.mainWindowOpen) return null;

  const totalWidth = size.width + (treeCollapsed ? 0 : treeWidth);

  return (
    <div ref={rootRef} onContextMenu={e => e.preventDefault()}
      style={{ position:'fixed', bottom:88, right:CHAT_RIGHT_PX, zIndex:4000, width:totalWidth, height:size.height,
      background:'#fff', borderRadius:14, boxShadow:'0 12px 40px rgba(0,0,0,0.3)', overflow:'visible',
      display:'flex', flexDirection:'column' }}>
      <div style={{ position:'absolute', inset:0, borderRadius:14, overflow:'hidden', display:'flex',
        flexDirection:'column', width:'100%', height:'100%' }}>
        {/* 크기조절 — 위쪽 변 전체, 왼쪽 변 전체, 왼쪽위 모서리 세 군데 어디를 끌어도 크기가 바뀐다
            (시각적 표시 없이 마우스 커서만 바뀌는 숨은 손잡이) */}
        <div onMouseDown={startResize('top')} title="크기 조절(세로)"
          style={{ position:'absolute', top:0, left:16, right:0, height:6, cursor:'ns-resize', zIndex:19 }} />
        <div onMouseDown={startResize('left')} title="크기 조절(대화화면 가로폭)"
          style={{ position:'absolute', top:16, left:0, bottom:0, width:6, cursor:'ew-resize', zIndex:19 }} />
        <div onMouseDown={startResize('corner')} title="크기 조절"
          style={{ position:'absolute', top:0, left:0, width:16, height:16, cursor:'nwse-resize', zIndex:20 }} />

        {/* 창 상단 헤더 */}
        <div style={{ padding:'10px 16px', background:'#1e293b', color:'#fff', display:'flex',
          alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <span style={{ fontWeight:700, fontSize:14 }}>💬 사내 메신저</span>
          <button onClick={chat.closeMainWindow}
            style={{ background:'none', border:'none', color:'#cbd5e1', fontSize:16, cursor:'pointer' }}>
            ✕
          </button>
        </div>

        {/* 본문: 좌측 대화상대 트리(폭 고정) + 우측 대화. 트리-대화내용 경계선의 폭 조절 손잡이는
            "왼쪽 변" 손잡이(대화화면 가로폭 B 조절, 항상 존재)와 위치가 겹쳐서 혼동을 주므로 제거함 —
            대화상대 목록 폭은 더 이상 드래그로 조절할 수 없고 고정폭(TREE_DEFAULT)만 사용한다. */}
        <div style={{ flex:1, minHeight:0, display:'flex', position:'relative' }}>
          {!treeCollapsed && (
            <>
              <ContactTree width={treeWidth} />
              <div style={{ width:1, flexShrink:0, background:'#eee' }} />
            </>
          )}
          <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', borderLeft:'1px solid #eee' }}>
            {chat.activeConversationId ? (
              <ConversationPane conversationId={chat.activeConversationId} />
            ) : (
              <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center',
                color:'#999', fontSize:13 }}>
                왼쪽에서 대화상대를 선택하세요.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 좌측 패널 접기/펼치기 — 헤더 바(위쪽 바깥으로 튀어나오지 않고 헤더 안쪽, top:9)에 걸치도록
          배치하고, 가로 위치는 트리/대화 화면 경계선에 맞춘다: 펼쳐진 상태에서는 트리 폭 바로
          안쪽(left: treeWidth-6, 기본폭 220일 때 214px)에, 접힌 상태에서는 창 왼쪽 바깥 테두리에
          살짝 걸치도록(left:-13) 위치한다. 눌러도 대화 화면 폭(B)은 그대로 유지되고, 창 전체
          폭만 트리 폭만큼 늘거나(펼침) 줄어든다(접힘). */}
      <button onClick={() => setTreeCollapsed(c => !c)} title={treeCollapsed ? '대화상대 목록 펼치기' : '대화상대 목록 접기'}
        style={{ position:'absolute', top:9, left: treeCollapsed ? -13 : (treeWidth - 6), zIndex:21,
          width:24, height:24, borderRadius:'50%', background:'#fff', border:'1px solid #ddd',
          boxShadow:'0 1px 4px rgba(0,0,0,0.25)', fontSize:11, color:'#555', cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center' }}>
        {treeCollapsed ? '◀' : '▶'}
      </button>
    </div>
  );
}

// ── 좌측 패널: 회사 → 부서 → 직급 이름 조직도 트리(부서별 접기/펼치기) + 단체대화방 목록 ──
function ContactTree({ width }) {
  const chat = useChat();
  const [members, setMembers] = useState([]);
  const [collapsedDepts, setCollapsedDepts] = useState(() => new Set()); // 기본은 전부 펼쳐짐
  // 회원 옆에 표시할 현재 상태(회의중/출장중/외근중/휴가중) — { username: status|null }
  const [statuses, setStatuses] = useState({});

  useEffect(() => {
    API.get('/messages/members').then(r => setMembers(r.data || [])).catch(() => {});
  }, []);

  // 대화상대 목록의 상태 배지도 홈 화면과 동일하게 1분마다 자동 갱신한다.
  useEffect(() => {
    if (members.length === 0) return;
    const usernames = members.map(m => m.username).filter(Boolean);
    if (usernames.length === 0) return;
    const fetchStatuses = () => {
      API.get('/user-status/bulk', { params: { usernames: usernames.join(',') } })
        .then(r => setStatuses(r.data || {}))
        .catch(() => {});
    };
    fetchStatuses();
    const timer = setInterval(fetchStatuses, 60 * 1000);
    return () => clearInterval(timer);
  }, [members]);

  const deptTree = useMemo(() => {
    const map = {};
    members.forEach(m => {
      const dept = m.department || '미배정';
      if (!map[dept]) map[dept] = [];
      map[dept].push(m);
    });
    const depts = Object.keys(map).sort((a, b) => {
      if (a === '미배정') return 1;
      if (b === '미배정') return -1;
      return a.localeCompare(b, 'ko');
    });
    return depts.map(dept => ({ dept, people: map[dept] }));
  }, [members]);

  const toggleDept = (dept) => setCollapsedDepts(prev => {
    const next = new Set(prev);
    next.has(dept) ? next.delete(dept) : next.add(dept);
    return next;
  });

  const [showGroupModal, setShowGroupModal] = useState(false);
  const groupChats = chat.conversations.filter(c => c.type === 'group');

  const handleRename = (e, conv) => {
    e.stopPropagation();
    const name = window.prompt('단체대화방 이름을 입력하세요.', conv.name || '');
    if (name && name.trim() && name.trim() !== conv.name) chat.renameGroup(conv.id, name.trim());
  };

  return (
    <div style={{ width, flexShrink:0, overflowY:'auto', background:'#fff' }}>
      <div style={{ padding:'12px 14px', borderBottom:'1px solid #eee', display:'flex',
        justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
        <span style={{ fontSize:14, fontWeight:700, color:'#222' }}>{COMPANY_NAME}</span>
        <button onClick={() => setShowGroupModal(true)}
          style={{ fontSize:11, color:'#185FA5', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>
          + 단체대화
        </button>
      </div>

      {deptTree.length === 0 ? (
        <div style={{ padding:16, fontSize:13, color:'#999', textAlign:'center' }}>등록된 회원이 없습니다.</div>
      ) : deptTree.map(({ dept, people }) => {
        const collapsed = collapsedDepts.has(dept);
        return (
          <div key={dept}>
            <div onClick={() => toggleDept(dept)}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', cursor:'pointer',
                fontSize:13, fontWeight:700, color:'#555', background:'#f8fafc' }}>
              <span style={{ width:12, textAlign:'center', color:'#999' }}>{collapsed ? '+' : '-'}</span>
              <span>{dept}</span>
            </div>
            {!collapsed && people.map(m => (
              <div key={m.username} onClick={() => chat.openDirectChat(m.username)}
                style={{ padding:'7px 14px 7px 30px', display:'flex', alignItems:'center', gap:6,
                  cursor:'pointer', fontSize:13, color:'#333' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f7fa'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ width:7, height:7, borderRadius:'50%', flexShrink:0,
                  background: chat.onlineUsers.has(m.username) ? '#22c55e' : '#d1d5db' }}
                  title={chat.onlineUsers.has(m.username) ? '접속중' : '오프라인'} />
                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {[m.position, m.name].filter(Boolean).join(' ')}
                </span>
                {statuses[m.username] && <StatusBadge status={statuses[m.username]} style={{ fontSize: 9, padding: '1px 6px', flexShrink: 0 }} />}
              </div>
            ))}
          </div>
        );
      })}

      {groupChats.length > 0 && (
        <div>
          <div style={{ padding:'7px 14px', fontSize:12, fontWeight:700, color:'#555', background:'#f8fafc' }}>
            단체대화방
          </div>
          {groupChats.map(c => (
            <div key={c.id} onClick={() => chat.openConversationWindow(c.id)}
              style={{ padding:'7px 14px', display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:12, color:'#333' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f7fa'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>💬 {c.name}</span>
              {c.unreadCount > 0 && (
                <span style={{ background:'#dc2626', color:'#fff', fontSize:9, fontWeight:700,
                  borderRadius:10, padding:'1px 5px' }}>{c.unreadCount}</span>
              )}
              <button onClick={e => handleRename(e, c)} title="이름 변경"
                style={{ background:'none', border:'none', color:'#999', fontSize:11, cursor:'pointer', padding:1 }}>
                ✏️
              </button>
            </div>
          ))}
        </div>
      )}

      {showGroupModal && (
        <GroupChatModal members={members} onClose={() => setShowGroupModal(false)}
          onCreate={async (name, usernames) => { await chat.createGroupChat(name, usernames); setShowGroupModal(false); }} />
      )}
    </div>
  );
}

function GroupChatModal({ members, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const toggle = (u) => setSelected(prev => {
    const next = new Set(prev);
    next.has(u) ? next.delete(u) : next.add(u);
    return next;
  });

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:6000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, width:360, maxWidth:'92vw',
        maxHeight:'80vh', display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ fontSize:16, fontWeight:700, margin:'0 0 14px' }}>👥 단체대화방 만들기</h3>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="대화방 이름"
          style={{ padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, marginBottom:12, boxSizing:'border-box' }} />
        <div style={{ fontSize:12, color:'#888', marginBottom:6 }}>참여할 회원 선택 ({selected.size}명)</div>
        <div style={{ flex:1, overflowY:'auto', border:'1px solid #eee', borderRadius:8, minHeight:120 }}>
          {members.map(m => (
            <label key={m.username} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
              fontSize:13, cursor:'pointer', borderBottom:'1px solid #f5f5f5' }}>
              <input type="checkbox" checked={selected.has(m.username)} onChange={() => toggle(m.username)} />
              {[m.position, m.name].filter(Boolean).join(' ')}
              <span style={{ marginLeft:'auto', fontSize:11, color:'#999' }}>{m.department}</span>
            </label>
          ))}
        </div>
        <div style={{ display:'flex', gap:8, marginTop:14 }}>
          <button onClick={onClose}
            style={{ flex:1, padding:10, background:'#fff', border:'1px solid #ddd', borderRadius:8, cursor:'pointer' }}>
            취소
          </button>
          <button disabled={selected.size < 2}
            onClick={() => onCreate(name, Array.from(selected))}
            style={{ flex:1, padding:10, background: selected.size < 2 ? '#aaa' : '#185FA5', color:'#fff', border:'none',
              borderRadius:8, cursor: selected.size < 2 ? 'not-allowed' : 'pointer', fontWeight:600 }}>
            만들기
          </button>
        </div>
        <p style={{ fontSize:11, color:'#999', marginTop:8 }}>본인 포함 3명 이상이어야 합니다(2명 이상 선택).</p>
      </div>
    </div>
  );
}

// ── 우측 패널: 선택된 대화방의 실제 채팅 화면 ────────────────────────────────────
function ConversationPane({ conversationId }) {
  const { user } = useAuth();
  const chat = useChat();

  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [memberMap, setMemberMap] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [readStatus, setReadStatus] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  const [showInvite, setShowInvite] = useState(false);

  const [callState, setCallState] = useState(null);
  const [incomingFrom, setIncomingFrom] = useState(null);

  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const callPeerRef = useRef(null);

  useEffect(() => {
    if (!conversationId) return;
    API.get('/messages/conversations').then(r => {
      setConv((r.data || []).find(c => c.id === conversationId) || null);
    }).catch(() => {});
  }, [conversationId]);

  useEffect(() => {
    API.get('/messages/members').then(r => {
      const map = {};
      (r.data || []).forEach(m => { map[m.username] = m; });
      setMemberMap(map);
    }).catch(() => {});
  }, []);

  const labelFor = useCallback((username, fallbackName) => {
    const info = (user && username === user.username) ? user : memberMap[username];
    if (!info) return fallbackName || username;
    const nameLine = [info.position, info.name].filter(Boolean).join(' ') || fallbackName || username;
    return info.department ? `${info.department} ${nameLine}` : nameLine;
  }, [user, memberMap]);

  useEffect(() => {
    if (!conversationId) return;
    API.get(`/messages/conversations/${conversationId}/read-status`).then(r => {
      const map = {};
      (r.data || []).forEach(row => { map[row.username] = row.last_read_message_id || 0; });
      setReadStatus(map);
    }).catch(() => {});
  }, [conversationId, conv?.members?.length]);

  const unreadCountFor = useCallback((messageId) => {
    return Object.values(readStatus).filter(lastRead => (lastRead || 0) < messageId).length;
  }, [readStatus]);

  // 서버에 읽음처리를 보낼 때마다 런처 버튼의 숫자 배지(ChatContext의 conversations[].unreadCount)도
  // 함께 0으로 비워준다. 대화상대를 새로 클릭해서 여는 경우(openConversationWindow)는 이미 그
  // 시점에 배지가 비워지지만, 이미 activeConversationId로 지정되어 있던 대화방을 런처 버튼으로
  // 다시 열 때는 openConversationWindow를 거치지 않으므로 여기서 비워주지 않으면 실제로는 읽었는데
  // (서버 last_read_message_id는 갱신됨) 배지 숫자만 그대로 남아있는 버그가 생긴다.
  const markRead = useCallback((lastMessageId) => {
    if (!conversationId || !lastMessageId) return;
    API.post(`/messages/conversations/${conversationId}/read`, { lastMessageId }).catch(() => {});
    if (chat && chat.markConversationRead) chat.markConversationRead(conversationId);
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) return;
    API.get(`/messages/conversations/${conversationId}/messages`).then(r => {
      const rows = r.data || [];
      setMessages(rows);
      if (rows.length) markRead(rows[rows.length - 1].id);
    }).catch(() => {});
  }, [conversationId, markRead]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const createPeerConnection = useCallback((peerUsername) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        const s = getSocket();
        s && s.emit('call:ice-candidate', { toUsername: peerUsername, candidate: e.candidate });
      }
    };
    pc.ontrack = (e) => {
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = e.streams[0];
    };
    pcRef.current = pc;
    return pc;
  }, []);

  const endCall = useCallback((notifyPeer) => {
    if (notifyPeer && callPeerRef.current) {
      const s = getSocket();
      s && s.emit('call:end', { toUsername: callPeerRef.current });
    }
    if (pcRef.current) { try { pcRef.current.close(); } catch (e) {} pcRef.current = null; }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    callPeerRef.current = null;
    setIncomingFrom(null);
    setCallState(null);
  }, []);

  const startCall = useCallback(async () => {
    if (!conv || conv.type !== 'direct' || !conv.peerUsername) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      const pc = createPeerConnection(conv.peerUsername);
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      callPeerRef.current = conv.peerUsername;
      const s = getSocket();
      s && s.emit('call:invite', {
        toUsername: conv.peerUsername, conversationId, sdp: offer, callerName: user?.name,
      });
      setCallState('calling');
    } catch (e) {
      alert('카메라/마이크에 접근할 수 없습니다: ' + e.message);
    }
  }, [conv, conversationId, user, createPeerConnection]);

  const acceptCall = useCallback(async () => {
    if (!incomingFrom) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      const pc = createPeerConnection(incomingFrom.fromUsername);
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      await pc.setRemoteDescription(new RTCSessionDescription(incomingFrom.sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      callPeerRef.current = incomingFrom.fromUsername;
      const s = getSocket();
      s && s.emit('call:answer', { toUsername: incomingFrom.fromUsername, sdp: answer });
      setCallState('in-call');
    } catch (e) {
      alert('카메라/마이크에 접근할 수 없습니다: ' + e.message);
      endCall(true);
    }
  }, [incomingFrom, createPeerConnection, endCall]);

  const declineCall = useCallback(() => {
    if (incomingFrom) {
      const s = getSocket();
      s && s.emit('call:decline', { toUsername: incomingFrom.fromUsername });
    }
    setIncomingFrom(null);
    setCallState(null);
  }, [incomingFrom]);

  useEffect(() => {
    if (!conversationId) return;
    const s = getSocket();
    if (!s) return;
    s.emit('conversation:join', { conversationId });

    const onNewMessage = (msg) => {
      if (msg.conversation_id !== conversationId) return;
      setMessages(prev => [...prev, msg]);
      markRead(msg.id);
    };
    const onMessageDeleted = (payload) => {
      if (payload.conversationId !== conversationId) return;
      setMessages(prev => prev.filter(m => m.id !== payload.messageId));
    };
    const onReadSync = (payload) => {
      if (payload.conversationId !== conversationId) return;
      if (user && payload.username === user.username) return;
      setReadStatus(prev => ({ ...prev, [payload.username]: payload.lastReadMessageId }));
    };
    const onCallInvite = (payload) => {
      if (payload.conversationId !== conversationId) return;
      setIncomingFrom(payload);
      setCallState('incoming');
    };
    const onCallAnswer = async (payload) => {
      if (!pcRef.current) return;
      await pcRef.current.setRemoteDescription(new RTCSessionDescription(payload.sdp));
      setCallState('in-call');
    };
    const onCallIce = async (payload) => {
      if (!pcRef.current || !payload.candidate) return;
      try { await pcRef.current.addIceCandidate(new RTCIceCandidate(payload.candidate)); } catch (e) {}
    };
    const onCallDecline = () => { endCall(false); alert('상대방이 통화를 거절했습니다.'); };
    const onCallEnd = () => endCall(false);

    s.on('message:new', onNewMessage);
    s.on('message:deleted', onMessageDeleted);
    s.on('message:read:sync', onReadSync);
    s.on('call:invite', onCallInvite);
    s.on('call:answer', onCallAnswer);
    s.on('call:ice-candidate', onCallIce);
    s.on('call:decline', onCallDecline);
    s.on('call:end', onCallEnd);
    return () => {
      s.off('message:new', onNewMessage);
      s.off('message:deleted', onMessageDeleted);
      s.off('message:read:sync', onReadSync);
      s.off('call:invite', onCallInvite);
      s.off('call:answer', onCallAnswer);
      s.off('call:ice-candidate', onCallIce);
      s.off('call:decline', onCallDecline);
      s.off('call:end', onCallEnd);
    };
  }, [conversationId, markRead, endCall, user]);

  // 대화방을 전환하거나 창을 닫을 때 통화 중이었다면 상대에게도 종료를 알림
  useEffect(() => () => endCall(true), [conversationId, endCall]);

  const sendText = () => {
    const text = input.trim();
    if (!text || !conversationId) return;
    const s = getSocket();
    if (!s) return;
    s.emit('message:send', { conversationId, type: 'text', content: text }, (res) => {
      if (!res || !res.ok) alert((res && res.message) || '전송에 실패했습니다.');
    });
    setInput('');
  };

  const uploadAndSend = async (file) => {
    if (!file || !conversationId) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await API.post('/messages/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { fileUrl, fileName, isImage } = r.data;
      const s = getSocket();
      s && s.emit('message:send',
        { conversationId, type: isImage ? 'image' : 'file', fileUrl, fileName },
        (res) => { if (!res || !res.ok) alert((res && res.message) || '전송에 실패했습니다.'); });
    } catch (e) {
      alert('파일 전송 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    Array.from(e.dataTransfer.files || []).forEach(uploadAndSend);
  };
  const onPaste = (e) => {
    const items = Array.from((e.clipboardData && e.clipboardData.items) || []);
    const imgItem = items.find(it => it.type && it.type.startsWith('image/'));
    if (imgItem) {
      e.preventDefault();
      const file = imgItem.getAsFile();
      if (file) uploadAndSend(file);
    }
  };

  // 메시지 삭제 = "내 화면에서만" 지우기. 내가 보낸 것이든 상대가 보낸 것이든 지울 수 있고,
  // 실제로는 서버가 message_hides에 표시만 남겨서 상대방 화면에는 그대로 남고 내 화면에서만
  // 사라진다(전체 삭제가 아님). 화면 반영은 서버가 되쏴주는 'message:deleted'(아래 onMessageDeleted)를
  // 통해 이루어진다.
  const deleteMessage = (messageId) => {
    setContextMenu(null);
    const s = getSocket();
    if (!s) return;
    s.emit('message:delete', { conversationId, messageId }, (res) => {
      if (!res || !res.ok) alert((res && res.message) || '삭제할 수 없습니다.');
    });
  };

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [contextMenu]);

  let headerTitle = conv ? conv.name : '불러오는 중...';
  if (conv && conv.type === 'direct' && conv.createdBy && user) {
    const otherUsername = conv.createdBy === user.username ? conv.peerUsername : user.username;
    headerTitle = `${labelFor(conv.createdBy)} → ${labelFor(otherUsername)}`;
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1, minHeight:0, position:'relative' }}>
      {/* 대화방 자체 헤더(제목 + 화상통화/초대) */}
      <div style={{ padding:'8px 14px', borderBottom:'1px solid #eee', display:'flex',
        alignItems:'center', justifyContent:'space-between', flexShrink:0, background:'#fafbfc' }}>
        <div style={{ minWidth:0 }}>
          {conv && (
            <div style={{ fontSize:10, color:'#999' }}>
              {conv.type === 'group' ? '단체대화방' : '대화요청자 → 대화상대'}
            </div>
          )}
          <div style={{ fontWeight:700, fontSize:13, color:'#222', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
            {headerTitle}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, flexShrink:0 }}>
          <button onClick={() => setShowInvite(true)} title="대화상대 초대"
            style={{ background:'none', border:'none', color:'#666', fontSize:14, cursor:'pointer' }}>
            👤+
          </button>
          {conv && conv.type === 'direct' && (
            <button onClick={startCall} disabled={!!callState} title="화상통화 걸기"
              style={{ background:'none', border:'none', color:'#666', fontSize:14,
                cursor: callState ? 'default' : 'pointer', opacity: callState ? 0.4 : 1 }}>
              📹
            </button>
          )}
        </div>
      </div>

      {/* 메시지 목록 (드래그앤드롭 영역) */}
      <div ref={listRef}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{ flex:1, overflowY:'auto', padding:14, position:'relative',
          background: dragOver ? '#eef2ff' : '#f8fafc' }}>
        {dragOver && (
          <div style={{ position:'absolute', inset:0, border:'3px dashed #185FA5', borderRadius:8,
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, color:'#185FA5',
            fontWeight:700, background:'rgba(255,255,255,0.85)', zIndex:10 }}>
            여기에 파일을 놓으면 전송됩니다
          </div>
        )}
        {messages.map(m => {
          const mine = m.sender_username === user?.username;
          const unread = mine ? unreadCountFor(m.id) : 0;
          return (
            <div key={m.id} style={{ display:'flex', flexDirection:'column',
              alignItems: mine ? 'flex-end' : 'flex-start', marginBottom:8 }}>
              <span style={{ fontSize:12, color:'#888', marginBottom:2 }}>
                {labelFor(m.sender_username, m.sender_name)}
              </span>
              <div style={{ display:'flex', alignItems:'flex-end', gap:4, flexDirection: mine ? 'row' : 'row-reverse' }}>
                {mine && (unread > 0
                  ? <span style={{ fontSize:9, color:'#f59e0b', fontWeight:700, flexShrink:0 }}>{unread}</span>
                  : <span style={{ fontSize:9, color:'#22c55e', fontWeight:700, flexShrink:0 }}>읽음</span>)}
                <div
                  onContextMenu={e => {
                    // 내가 보낸 것/상대가 보낸 것 상관없이 누구나 우클릭으로 삭제할 수 있다
                    // ("내 화면에서만" 지워지는 방식이라 상대에게 영향이 없기 때문).
                    e.preventDefault();
                    // 메뉴의 오른쪽 끝을 클릭한 x좌표가 아니라 "대화내용" 목록의 오른쪽 끝선(패딩
                    // 안쪽, 말풍선이 정렬되는 기준선)에 맞춘다 — 메시지가 창 오른쪽 가장자리에
                    // 가깝게 붙어 있을 때 메뉴가 창 밖으로 삐져나가 잘려 보이던 문제를 없앤다.
                    const listRect = listRef.current ? listRef.current.getBoundingClientRect() : null;
                    const right = listRect ? (window.innerWidth - listRect.right + 14) : (window.innerWidth - e.clientX);
                    setContextMenu({ right, y: e.clientY, messageId: m.id });
                  }}
                  style={{ maxWidth:280, padding:'8px 12px', borderRadius:14, fontSize:14,
                    background: mine ? '#DAE3F3' : '#fff', color: '#000',
                    border: mine ? 'none' : '1px solid #e5e7eb', wordBreak:'break-word' }}>
                  {m.type === 'text' && m.content}
                  {m.type === 'image' && (
                    <img src={FILE_BASE + m.file_url} alt={m.file_name || '사진'}
                      onClick={() => setPreviewImage(FILE_BASE + m.file_url)}
                      style={{ maxWidth:220, maxHeight:220, borderRadius:8, display:'block', cursor:'zoom-in' }} />
                  )}
                  {m.type === 'file' && (
                    <a href={FILE_BASE + m.file_url} download={m.file_name || true}
                      style={{ color:'#185FA5', textDecoration:'underline' }}>
                      📎 {m.file_name || '파일'}
                    </a>
                  )}
                </div>
              </div>
              <span style={{ fontSize:12, color:'#aaa', marginTop:2 }}>
                {(m.created_at || '').replace('T', ' ').slice(0, 16)}
              </span>
            </div>
          );
        })}
      </div>

      {/* 입력창 */}
      <div style={{ display:'flex', gap:6, padding:10, borderTop:'1px solid #e5e7eb',
        background:'#fff', flexShrink:0, alignItems:'flex-end' }}>
        <input ref={fileInputRef} type="file" style={{ display:'none' }}
          onChange={e => { const f = e.target.files && e.target.files[0]; if (f) uploadAndSend(f); e.target.value = ''; }} />
        <button onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={uploading}
          title="파일/사진 첨부"
          style={{ background:'#f1f5f9', border:'none', borderRadius:8, width:32, height:32,
            fontSize:14, cursor:'pointer', flexShrink:0 }}>
          📎
        </button>
        <textarea value={input} onChange={e => setInput(e.target.value)} onPaste={onPaste}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); } }}
          placeholder="대화내용 입력 (붙여넣기로 스크린샷 전송)"
          style={{ flex:1, resize:'none', height:32, padding:'6px 8px', border:'1px solid #ddd',
            borderRadius:8, fontSize:12, fontFamily:'inherit' }} />
        <button onClick={sendText} disabled={!input.trim()}
          style={{ background: input.trim() ? '#185FA5' : '#cbd5e1', color:'#fff', border:'none',
            borderRadius:8, padding:'0 12px', height:32, fontSize:12, fontWeight:600,
            cursor: input.trim() ? 'pointer' : 'default' }}>
          전송
        </button>
      </div>
      {uploading && (
        <div style={{ position:'absolute', top:56, left:'50%', transform:'translateX(-50%)',
          background:'#333', color:'#fff', fontSize:11, padding:'5px 12px', borderRadius:20 }}>
          업로드 중...
        </div>
      )}

      {/* 메시지 우클릭 삭제 메뉴 */}
      {contextMenu && (
        <div style={{ position:'fixed', top:contextMenu.y, right:contextMenu.right, zIndex:6500,
          background:'#fff', borderRadius:8, boxShadow:'0 6px 20px rgba(0,0,0,0.25)', overflow:'hidden' }}
          onClick={e => e.stopPropagation()}>
          <button onClick={() => deleteMessage(contextMenu.messageId)}
            style={{ display:'block', width:'100%', padding:'8px 16px', background:'none', border:'none',
              color:'#dc2626', fontSize:12, cursor:'pointer', textAlign:'left', whiteSpace:'nowrap' }}>
            🗑 삭제 (내 화면에서만 삭제됩니다)
          </button>
        </div>
      )}

      {/* 사진 미리보기 */}
      {previewImage && (
        <div onClick={() => setPreviewImage(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:6600,
            display:'flex', alignItems:'center', justifyContent:'center', cursor:'zoom-out' }}>
          <img src={previewImage} alt="사진 미리보기" onClick={e => e.stopPropagation()}
            style={{ maxWidth:'90vw', maxHeight:'90vh', borderRadius:8 }} />
          <button onClick={() => setPreviewImage(null)}
            style={{ position:'absolute', top:20, right:24, background:'none', border:'none',
              color:'#fff', fontSize:28, cursor:'pointer' }}>
            ✕
          </button>
        </div>
      )}

      {/* 대화상대 초대 모달 */}
      {showInvite && (
        <InviteModal
          excludeUsernames={(conv?.members || []).map(m => m.username)}
          onClose={() => setShowInvite(false)}
          onInvite={async (usernames) => {
            await chat.inviteToConversation(conversationId, usernames);
            setShowInvite(false);
          }}
        />
      )}

      {/* 화상통화 오버레이 */}
      {callState && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:7000,
          display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
          {callState === 'incoming' ? (
            <div style={{ textAlign:'center', color:'#fff' }}>
              <div style={{ fontSize:40, marginBottom:12 }}>📹</div>
              <div style={{ fontSize:16, marginBottom:24 }}>
                {incomingFrom?.fromName || incomingFrom?.fromUsername}님이 화상통화를 겁니다
              </div>
              <div style={{ display:'flex', gap:16, justifyContent:'center' }}>
                <button onClick={acceptCall}
                  style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:24,
                    padding:'10px 24px', fontSize:14, fontWeight:700, cursor:'pointer' }}>
                  받기
                </button>
                <button onClick={declineCall}
                  style={{ background:'#dc2626', color:'#fff', border:'none', borderRadius:24,
                    padding:'10px 24px', fontSize:14, fontWeight:700, cursor:'pointer' }}>
                  거절
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ position:'relative', width:'90%', maxWidth:640, aspectRatio:'16/9',
                background:'#111', borderRadius:12, overflow:'hidden' }}>
                <video ref={remoteVideoRef} autoPlay playsInline
                  style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                <video ref={localVideoRef} autoPlay playsInline muted
                  style={{ position:'absolute', bottom:12, right:12, width:120, borderRadius:8,
                    border:'2px solid #fff', objectFit:'cover' }} />
                {callState === 'calling' && (
                  <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center',
                    justifyContent:'center', color:'#fff', fontSize:14 }}>
                    연결 중...
                  </div>
                )}
              </div>
              <button onClick={() => endCall(true)}
                style={{ marginTop:20, background:'#dc2626', color:'#fff', border:'none',
                  borderRadius:24, padding:'10px 28px', fontSize:14, fontWeight:700, cursor:'pointer' }}>
                🔴 통화 종료
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function InviteModal({ excludeUsernames, onClose, onInvite }) {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    API.get('/messages/members').then(r => setMembers(r.data || [])).catch(() => {});
  }, []);

  const toggle = (u) => setSelected(prev => {
    const next = new Set(prev);
    next.has(u) ? next.delete(u) : next.add(u);
    return next;
  });

  const candidates = members.filter(m => !excludeUsernames.includes(m.username));

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:6800,
      display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, width:340, maxWidth:'92vw',
        maxHeight:'80vh', display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ fontSize:15, fontWeight:700, margin:'0 0 12px' }}>👤+ 대화상대 초대</h3>
        <div style={{ flex:1, overflowY:'auto', border:'1px solid #eee', borderRadius:8, minHeight:100 }}>
          {candidates.length === 0 ? (
            <div style={{ padding:16, fontSize:13, color:'#999', textAlign:'center' }}>초대할 수 있는 회원이 없습니다.</div>
          ) : candidates.map(m => (
            <label key={m.username} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
              fontSize:13, cursor:'pointer', borderBottom:'1px solid #f5f5f5' }}>
              <input type="checkbox" checked={selected.has(m.username)} onChange={() => toggle(m.username)} />
              {[m.position, m.name].filter(Boolean).join(' ')}
              <span style={{ marginLeft:'auto', fontSize:11, color:'#999' }}>{m.department}</span>
            </label>
          ))}
        </div>
        <p style={{ fontSize:11, color:'#999', margin:'8px 0' }}>
          1:1 대화방에 초대해서 3명 이상이 되면 자동으로 단체대화방으로 바뀝니다.
        </p>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onClose}
            style={{ flex:1, padding:10, background:'#fff', border:'1px solid #ddd', borderRadius:8, cursor:'pointer' }}>
            취소
          </button>
          <button disabled={selected.size === 0}
            onClick={() => onInvite(Array.from(selected))}
            style={{ flex:1, padding:10, background: selected.size === 0 ? '#aaa' : '#185FA5', color:'#fff', border:'none',
              borderRadius:8, cursor: selected.size === 0 ? 'not-allowed' : 'pointer', fontWeight:600 }}>
            초대
          </button>
        </div>
      </div>
    </div>
  );
}
